import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { api } from '../../../singleton/axios';

interface DeleteAllHoldingsResponse {
  message: string;
  deleted_count: number;
  cache_keys_cleared: number;
}

async function deleteAllHoldings(): Promise<DeleteAllHoldingsResponse> {
  try {
    const { data } = await api.delete<DeleteAllHoldingsResponse>('/portfolio/all-holdings');
    return data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || 'Failed to delete all holdings';
    const errorCode = error.response?.data?.code || 'UNKNOWN_ERROR';

    throw new Error(`${errorMessage} (${errorCode})`);
  }
}

export function useDeleteAllHoldings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteAllHoldings(),
    onSuccess: (response) => {
      // Remove portfolio queries entirely to force fresh fetch on navigation
      queryClient.removeQueries({ queryKey: ['portfolio'] });
      queryClient.removeQueries({ queryKey: ['currentPortfolio'] });
      queryClient.removeQueries({ queryKey: ['portfolioPrices'] });

      // Invalidate dividend-related queries as they depend on portfolio holdings
      queryClient.invalidateQueries({ queryKey: ['dividendAnalysis'] });
      queryClient.invalidateQueries({ queryKey: ['dividendStats'] });
      queryClient.invalidateQueries({ queryKey: ['portfolioDividendStats'] });
      queryClient.invalidateQueries({ queryKey: ['dividendCalendar'] });

      // Show success notification
      const count = response.deleted_count;
      enqueueSnackbar(`All portfolio holdings deleted! (${count} ${count === 1 ? 'record' : 'records'} removed)`, {
        variant: 'success',
        autoHideDuration: 4000,
      });
    },
    onError: (error: Error) => {
      // Show error notification
      enqueueSnackbar(error.message || 'Failed to delete all holdings', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
  });
}

export type { DeleteAllHoldingsResponse };